import React, { FormEvent, useState } from 'react';
import CurrentAccount from '../../../models/CurrentAccount';
import ElectronicStock from '../../../models/ElectronicStock';
import Pagination from '../../../models/Pagination';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, TableContainer, Stack, Collapse, alpha, Button, Typography } from '@mui/material';
import { ArrowDropDownOutlined, ArrowDropUpOutlined } from '@mui/icons-material';
import PaginationButtons from '../../PaginationButtons';
import { formattedCurrency } from '../../../utils/StringUtils';
import moment from 'moment';
import { ISTableTitle, ISTableHeaderCell, ISTableRow } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../theme/ThemeContext';
import { receiptLegalTypes } from '../../GeneralDesc';
import { getToken } from '../../../utils/fetchApi';
import { FiletypePdf, FiletypeXls } from 'react-bootstrap-icons';

export interface Row {
  data: CurrentAccount
  action?: 'create' | 'update' | 'error';
}

export interface RowES {
  data: ElectronicStock
  action?: 'create' | 'update' | 'error';
}


interface Props {
  rows: Row[];
  electronicStockRows: RowES[];
  loading: boolean;
  pagination?: Pagination;
  fullInfo?: boolean;
  adminInfo?: boolean;
  fromClientView?: boolean;
  codigo_cliente?: string;
  onChangePage?: (page: number) => void;
  showDownloadButton?: boolean;
  onClickInfo?: (currentAccount: CurrentAccount) => void;
  onClickDownload?: (row: Row) => void;
}

const CurrentAccounts: React.FC<Props> = (props) => {
  const showDownloadButton = props.showDownloadButton || false;
  const { t } = useTranslation('current')
  const { theme } = useThemeContext()
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.adminInfo)
  const [showElectronicStockRows, setShowElectronicStockRows] = useState<boolean>(false);

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  }

  const DownloadCurrentAccountListButton = () => {
    const canShare = () => navigator.canShare?.()
    const handleDownloadAndShare = async (fileType: string) => {
      const url = props.adminInfo ? `${process.env.REACT_APP_API_URL}/dlCurrentAccounts?access_token=${getToken()}&codigo_cliente=${props.codigo_cliente}&fileType=${fileType}` :
        `${process.env.REACT_APP_API_URL}/dlCurrentAccounts?access_token=${getToken()}&fileType=${fileType}`;

      try {

        // 1. Fetch modificado para evitar la descarga automática
        const response = await fetch(url);

        // 2. Obtener el blob y el nombre del archivo del header
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileNameMatch = contentDisposition?.match(/filename=(.+)/);

        const fileName = fileNameMatch ? fileNameMatch[1] : `cuenta-corriente-${props.codigo_cliente}-${moment().tz("America/Argentina/Buenos_Aires").format('YYYY-MM-DD')}-industrias_saladillo.${fileType}`;

        // 3. Crear el File con el tipo MIME correcto
        const file = new File([blob], fileName, {
          type: fileType === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Verificar específicamente si se puede compartir el archivo
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: t('downloadDL', { ns: 'others' }),
            text: t('shareDLText', { ns: 'others' }) || 'Compartir cuenta corriente'
          });
          return
        } else {
          window.open(
            url,
            '_blank'
          );
        }
      } catch (error) {
        console.error('Error al descargar/compartir:', error);
        // 4. Fallback final (tu método original))
        window.open(
          url,
          '_blank'
        );
      }
    };


    return (
      <Stack display="flex" flexDirection='row' justifyContent="flex-end" padding="10px" paddingTop="20px">
        <Button

          id="download"
          aria-label="download"
          size='large'
          style={{ textTransform: 'none', marginRight: '30px' }}
          onClick={() => handleDownloadAndShare('pdf')}
        >
          {t(canShare() ? 'shareDLText' : 'downloadDL', { ns: 'others' })}&nbsp;&nbsp;<FiletypePdf size={30} />
        </Button>
        <Button

          id="download"
          aria-label="download"
          size='large'
          style={{ textTransform: 'none' }}
          onClick={() => handleDownloadAndShare('xls')}
        >
          {t('downloadDL', { ns: 'others' })}&nbsp;&nbsp;<FiletypeXls size={30} />
        </Button>
      </Stack>
    );
  }

  const rowsToShow = /* !props.fullInfo ? props.rows.slice(0, 5) : */ props.rows

  let balance: { [key: string]: number } = {};
  balance['1'] = 0
  balance['3'] = 0
  balance['4'] = 0
  let ccc = '0'

  return (
    <Stack
      style={{ display: 'flex', flexDirection: 'column', height: props.fullInfo ? !props.adminInfo ? 'calc(100% - var(--header-height)' : '' : '100%', borderRadius: "10px", margin: "10px", minWidth: props.fullInfo ? "500px" : "770px" }}
      onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); props.adminInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true) }}>

      <ISTableTitle title={t('title')}
        sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          {props.adminInfo && showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            props.adminInfo ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> : <></>}
        </Stack>
      </ISTableTitle>
      <Collapse style={{ padding: props.adminInfo ? "10px" : 'inherit' }} in={props.fromClientView ? true : props.adminInfo ? showFullInfo || showElectronicStockRows : true}>


        <TableContainer sx={{ overflowX: 'auto', height: !props.fullInfo ? '275px' : '100%' }}>
          {!props.loading &&
            <Table size="medium" stickyHeader={props.fullInfo && !props.adminInfo}>
              <TableHead >
                <TableRow selected={true}>
                  <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center'>{t('invoice')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center'>{t('reference')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center'>{t('dueDate')}</ISTableHeaderCell>
                  <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='right'>{t('debit')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='right'>{t('credit')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='right'>{t('balance')}{`\u00A0\u00A0`}</ISTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!props.loading && rowsToShow.map((row, index) => {
                  let result = <></>
                  if (index === 0) {
                    balance['1'] = 0
                    balance['4'] = 0
                  }
                  if (ccc !== '0' && ccc !== row.data.codigo_cuentacorriente) {
                    result = <ISTableRow key={index + 'r'} color={mapActionToColor(row.action)} sx={{ backgroundColor: alpha(theme.palette.info.dark, 0.1) }}>
                      <TableCell colSpan={4} sx={{ backgroundColor: theme.palette.background.default }} />
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('total')} {t('title')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align='right' sx={{ fontWeight: 'bold' }}>{formattedCurrency(Math.abs(balance[ccc]))}{balance[ccc] >= 0 ? `\u00A0\u00A0` : '-'}</TableCell>
                    </ISTableRow>
                  }
                  ccc = row.data.codigo_cuentacorriente
                  balance[row.data.codigo_cuentacorriente] += (Number(row.data.importe) as number)
                  return (<>
                    {result}
                    <ISTableRow key={index} color={mapActionToColor(row.action)}
                      sx={{ cursor: //row.data.codigo_cuentacorriente === '1' && 
                        (receiptLegalTypes.includes(row.data.comprobante.charAt(0)) || row.data.comprobante.includes('CR')) ? 'pointer' : 'normal' }}
                      onClick={(e) => {
                        e.stopPropagation(); //row.data.codigo_cuentacorriente === '1' && 
                        (receiptLegalTypes.includes(row.data.comprobante.charAt(0)) || row.data.comprobante.includes('CR')) &&
                          showDownloadButton && props.onClickInfo && props.onClickInfo(row.data)
                      }}>
                      <TableCell align='center' sx={{ borderSpacing: "18px 12px" }}>{moment(row.data.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                      <TableCell align='center'>{row.data.comprobante}</TableCell>
                      <TableCell align='center'>{row.data.referencia}</TableCell>
                      <TableCell align='center'>{row.data.vencimiento ? moment(row.data.vencimiento).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY') : ''}</TableCell>
                      <TableCell>{row.data.texto1}</TableCell>
                      <TableCell align='right'>{row.data.importe >= 0 ? formattedCurrency(row.data.importe) : ''}</TableCell>
                      <TableCell align='right'>{row.data.importe < 0 ? formattedCurrency(Math.abs(row.data.importe)) : ''}</TableCell>
                      <TableCell align='right'>{formattedCurrency(Math.abs(balance[row.data.codigo_cuentacorriente]))}{balance[row.data.codigo_cuentacorriente] >= 0 ? `\u00A0\u00A0` : '-'}</TableCell>
                    </ISTableRow></>)
                }
                )}
                {!props.loading &&
                  (ccc !== '0' && ccc !== '1') &&
                  <ISTableRow key={`ccc${ccc}`} color={mapActionToColor('')} sx={{ backgroundColor: alpha(theme.palette.info.dark, 0.1) }}>
                    <TableCell colSpan={4} sx={{ borderBottom: "none", backgroundColor: theme.palette.background.default }} />
                    {/*<TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />*/}
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('total')} {t('bouncedChecks')}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 'bold' }} align='right'>{formattedCurrency(Math.abs(balance[ccc]))}{balance[ccc] >= 0 ? `\u00A0\u00A0` : '-'}</TableCell>
                  </ISTableRow>
                }
                {/*</Collapse>*/}

                {!props.loading &&
                  (ccc !== '0') &&
                  <ISTableRow key={'total'} color={mapActionToColor('')} sx={{ backgroundColor: alpha(theme.palette.info.dark, 0.2) }}>
                    <TableCell colSpan={4} sx={{ borderBottom: "none", backgroundColor: theme.palette.background.default }} />
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('total')}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 'bold' }} align='right'>{formattedCurrency(Math.abs(balance['1'] + balance['4']))}{balance['1'] + balance['4'] >= 0 ? `\u00A0\u00A0` : '-'}</TableCell>
                  </ISTableRow>
                }
                {props.electronicStockRows && props.electronicStockRows.length > 0 &&
                  <ISTableRow key={'electronicStockHeader'} color={'default'}>
                    <TableCell colSpan={8} sx={{ borderBottom: "none", backgroundColor: theme.palette.background.default }}></TableCell>
                  </ISTableRow>
                }
                {props.electronicStockRows && props.electronicStockRows.length > 0 &&
                  <TableRow  key={'electronicStockHeaderRow'} selected={true} style={{ borderTop: "1px solid", display: showElectronicStockRows ? 'table-row' : 'none' }}>
                    <ISTableHeaderCell colSpan={2} align='center'>
                      {t('origin')}</ISTableHeaderCell>
                    <ISTableHeaderCell align='center'>{t('reference')}</ISTableHeaderCell>
                    <ISTableHeaderCell align='center'>{t('dueDate')}</ISTableHeaderCell>
                    <ISTableHeaderCell colSpan={3}>{t('bank')}</ISTableHeaderCell>
                    <ISTableHeaderCell align='right'>{t('value')}{`\u00A0\u00A0`}</ISTableHeaderCell>
                  </TableRow>
                }
                {props.electronicStockRows && props.electronicStockRows.length > 0 &&
                  props.electronicStockRows.map((row, index) => {
                    if (index === 0) {
                      balance['3'] = 0
                    }
                    ccc = '3'
                    balance['3'] += (Number(row.data.importe) as number)
                    return (
                      <ISTableRow key={`3-${index}`} color={mapActionToColor(row.action)} style={{ display: showElectronicStockRows ? 'table-row' : 'none' }}>
                        <TableCell colSpan={2} align='center' sx={{ borderSpacing: "18px 12px" }}>
                          {row.data.origen === 'COBRANZA INTEGRADA' ? 'OTROS VALORES' : row.data.origen}</TableCell>
                        <TableCell align='center'>{row.data.numero}</TableCell>
                        <TableCell align='center'>{row.data.vencimiento ? moment(row.data.vencimiento).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY') : ''}</TableCell>
                        <TableCell colSpan={3}>{row.data.banco}</TableCell>
                        <TableCell align='right'>{formattedCurrency(Math.abs(row.data.importe))}</TableCell>
                      </ISTableRow>
                    )
                  })}
                {!props.loading && props.electronicStockRows && props.electronicStockRows.length > 0 &&
                  <ISTableRow key={'r3'} color={mapActionToColor('')} sx={{ backgroundColor: alpha(theme.palette.info.dark, 0.1) }}
                    onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowElectronicStockRows(!showElectronicStockRows) }}>
                    <TableCell colSpan={4} sx={{ borderBottom: "none", borderTop: "none", backgroundColor: theme.palette.background.default }} />
                    <TableCell sx={{ borderTop: "1px solid" }}>
                      <Typography variant='body2' sx={{ display: 'flex', flexDirection: 'row', fontWeight: 'bold' }}>{t('total')} {t('electronicStock')}{`\u00A0\u00A0`}
                        {!showElectronicStockRows ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
                          <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderTop: "1px solid" }} />
                    <TableCell sx={{ borderTop: "1px solid" }} />
                    <TableCell align='right' sx={{ borderTop: "1px solid", fontWeight: 'bold' }}>{formattedCurrency(Math.abs(balance['3']))}</TableCell>
                  </ISTableRow>
                }
              </TableBody>
              {!props.loading && props.electronicStockRows && props.electronicStockRows.length > 0 &&
                <ISTableRow key={'electronicStockFooter'} color={'default'}>
                  <TableCell size='small' colSpan={4} sx={{ borderBottom: "none", borderTop: "none", backgroundColor: theme.palette.background.default }}/>
                  <TableCell size='small' colSpan={4} sx={{ borderBottom: "none", borderTop: "none", backgroundColor: theme.palette.background.default }}>
                    <Typography sx={{ whiteSpace: 'pre-line' }} variant='caption'>{t('footNotes.footNote1') + '\n' + t('footNotes.footNote2')}</Typography>
                  </TableCell>
                </ISTableRow>
              }
            </Table>
          }

          {props.loading &&
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={25} />
            </div>
          }
          {props.fullInfo && <PaginationButtons pagination={props.pagination} changePage={props.onChangePage} />}
          <DownloadCurrentAccountListButton />
        </TableContainer>
      </Collapse>
    </Stack >
  );
}

export default CurrentAccounts
//.sort((a, b) => a && b ? a.data.codigo_cuentacorriente > b.data.codigo_cuentacorriente ? 1 : -1 : 0)